import { useSearchParams } from "react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IoClose } from "react-icons/io5";
import { MdFilterList } from "react-icons/md";
import { useIsMobile } from "~/hook/use-mobile";
import { useFontStyle } from "~/hook/useFontStyle";
import useLocale from "~/hook/useLocale";
import { selectedFilterDataType } from "~/interface/kirtan-lyrics/kirtan-lyrics";
import {
  setSelectedBhav,
  setSelectedKBook,
  setSelectedKPlace,
  setSelectedKPrasang,
  setSelectedRaagDhal,
  setSelectedRachayta,
} from "~/lib/store/slice/kirtanLyricsSlice";
import { useAppDispatch, useAppSelector } from "~/lib/store/store";
import { cn } from "~/lib/utils";
import { Checkbox } from "../ui/checkbox";
import { SheetClose } from "../ui/sheet";
import { Search } from "lucide-react";

interface KirtanLyricsFilterMobileProps {
  onFilterApplied?: () => void;
}

interface FilterCategory {
  key: string;
  title: string;
  data: selectedFilterDataType[];
  tempSelected: selectedFilterDataType[];
  setTempSelected: React.Dispatch<React.SetStateAction<selectedFilterDataType[]>>;
}

const KirtanLyricsFilterMobile = ({ onFilterApplied }: KirtanLyricsFilterMobileProps) => {
  const {
    bhav,
    raagDhaal,
    rachayta,
    k_book,
    k_place,
    k_prasang,
    selectedBhav,
    selectedRaagDhal,
    selectedRachayta,
    selectedKBook,
    selectedKPlace,
    selectedKPrasang,
  } = useAppSelector((state) => state.kirtanLyrics);
  const { medium, normalInter } = useFontStyle();
  const dispatch = useAppDispatch();
  const { navigate } = useLocale();
  const { t } = useTranslation();
  const [, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();

  const [tempSelectedRachayta, setTempSelectedRachayta] = useState<selectedFilterDataType[]>([]);
  const [tempSelectedRaagDhal, setTempSelectedRaagDhal] = useState<selectedFilterDataType[]>([]);
  const [tempSelectedBhav, setTempSelectedBhav] = useState<selectedFilterDataType[]>([]);
  const [tempSelectedKBook, setTempSelectedKBook] = useState<selectedFilterDataType[]>([]);
  const [tempSelectedKPlace, setTempSelectedKPlace] = useState<selectedFilterDataType[]>([]);
  const [tempSelectedKPrasang, setTempSelectedKPrasang] = useState<selectedFilterDataType[]>([]);

  const [searchTerms, setSearchTerms] = useState<{ [key: string]: string }>({});
  const [activeCategory, setActiveCategory] = useState<string>("");

  // Sync temp state with currently selected items from Redux on mount and when selections change
  useEffect(() => {
    if (isMobile) {
      setTempSelectedRachayta(selectedRachayta);
      setTempSelectedRaagDhal(selectedRaagDhal);
      setTempSelectedBhav(selectedBhav);
      setTempSelectedKBook(selectedKBook);
      setTempSelectedKPlace(selectedKPlace);
      setTempSelectedKPrasang(selectedKPrasang);

      // Set first category with data as active
      const firstCategoryWithData =
        rachayta.length > 0
          ? "rachayta"
          : raagDhaal.length > 0
            ? "raagdhal"
            : bhav.length > 0
              ? "bhav"
              : k_book.length > 0
                ? "k_book"
                : k_place.length > 0
                  ? "k_place"
                  : k_prasang.length > 0
                    ? "k_prasang"
                    : "";

      setActiveCategory(firstCategoryWithData);
    }
  }, [
    isMobile,
    selectedRachayta,
    selectedRaagDhal,
    selectedBhav,
    selectedKBook,
    selectedKPlace,
    selectedKPrasang,
    rachayta.length,
    raagDhaal.length,
    bhav.length,
    k_book.length,
    k_place.length,
    k_prasang.length,
  ]);

  const categories: FilterCategory[] = [
    {
      key: "rachayta",
      title: t("kirtan-lyrics.rachayta"),
      data: rachayta,
      tempSelected: tempSelectedRachayta,
      setTempSelected: setTempSelectedRachayta,
    },
    {
      key: "raagdhal",
      title: t("kirtan-lyrics.raag_dhal"),
      data: raagDhaal,
      tempSelected: tempSelectedRaagDhal,
      setTempSelected: setTempSelectedRaagDhal,
    },
    { key: "bhav", title: t("kirtan-lyrics.bhav"), data: bhav, tempSelected: tempSelectedBhav, setTempSelected: setTempSelectedBhav },
    { key: "k_book", title: t("kirtan-lyrics.k_book"), data: k_book, tempSelected: tempSelectedKBook, setTempSelected: setTempSelectedKBook },
    { key: "k_place", title: t("kirtan-lyrics.k_place"), data: k_place, tempSelected: tempSelectedKPlace, setTempSelected: setTempSelectedKPlace },
    {
      key: "k_prasang",
      title: t("kirtan-lyrics.k_prasang"),
      data: k_prasang,
      tempSelected: tempSelectedKPrasang,
      setTempSelected: setTempSelectedKPrasang,
    },
  ];

  const activeCategoryData = categories.find((cat) => cat.key === activeCategory);

  const handleToggleItem = (item: selectedFilterDataType) => {
    if (!activeCategoryData) return;

    const isSelected = activeCategoryData.tempSelected.some((i) => i.id === item.id);

    if (isSelected) {
      activeCategoryData.setTempSelected(activeCategoryData.tempSelected.filter((i) => i.id !== item.id));
    } else {
      activeCategoryData.setTempSelected([...activeCategoryData.tempSelected, item]);
    }
  };

  const getFilteredItems = () => {
    if (!activeCategoryData) return [];

    const searchTerm = searchTerms[activeCategory]?.toLowerCase() || "";
    return activeCategoryData.data.filter((item) => item?.text?.toLowerCase().includes(searchTerm));
  };

  const handleClearAll = () => {
    setTempSelectedRachayta([]);
    setTempSelectedRaagDhal([]);
    setTempSelectedBhav([]);
    setTempSelectedKBook([]);
    setTempSelectedKPlace([]);
    setTempSelectedKPrasang([]);
    setSearchTerms({});
  };

  const handleApplyFilters = () => {
    // Update Redux state
    dispatch(setSelectedRachayta(tempSelectedRachayta));
    dispatch(setSelectedRaagDhal(tempSelectedRaagDhal));
    dispatch(setSelectedBhav(tempSelectedBhav));
    dispatch(setSelectedKBook(tempSelectedKBook));
    dispatch(setSelectedKPlace(tempSelectedKPlace));
    dispatch(setSelectedKPrasang(tempSelectedKPrasang));

    // Build URL params
    const newParams = new URLSearchParams();

    if (tempSelectedRachayta.length > 0) {
      newParams.set(
        "rachayta",
        tempSelectedRachayta
          .map((i) => i.text)
          .join(",")
          .toLowerCase(),
      );
    }
    if (tempSelectedRaagDhal.length > 0) {
      newParams.set(
        "raagdhal",
        tempSelectedRaagDhal
          .map((i) => i.text)
          .join(",")
          .toLowerCase(),
      );
    }
    if (tempSelectedBhav.length > 0) {
      newParams.set(
        "bhav",
        tempSelectedBhav
          .map((i) => i.text)
          .join(",")
          .toLowerCase(),
      );
    }
    if (tempSelectedKBook.length > 0) {
      newParams.set(
        "k_book",
        tempSelectedKBook
          .map((i) => i.text)
          .join(",")
          .toLowerCase(),
      );
    }
    if (tempSelectedKPlace.length > 0) {
      newParams.set(
        "k_place",
        tempSelectedKPlace
          .map((i) => i.text)
          .join(",")
          .toLowerCase(),
      );
    }
    if (tempSelectedKPrasang.length > 0) {
      newParams.set(
        "k_prasang",
        tempSelectedKPrasang
          .map((i) => i.text)
          .join(",")
          .toLowerCase(),
      );
    }

    // Update search params
    setSearchParams(newParams, { replace: true });

    // Remove kirtanId from URL
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.delete("kirtanId");
      window.history.replaceState(null, "", url.toString());
      window.dispatchEvent(new CustomEvent("kirtanIdChanged", { detail: { kirtanId: null } }));
    }

    if (onFilterApplied) {
      onFilterApplied();
    }

    if (navigate) {
      navigate(`kirtan-lyrics?${newParams.toString()}`, { replace: true });
    }
  };

  const getTotalSelectedCount = () => {
    return (
      tempSelectedRachayta.length +
      tempSelectedRaagDhal.length +
      tempSelectedBhav.length +
      tempSelectedKBook.length +
      tempSelectedKPlace.length +
      tempSelectedKPrasang.length
    );
  };

  return (
    <div className="w-full h-full flex flex-col justify-between">
      {/* Header */}
      <div className="flex flex-col h-[90%]">
        <div className="px-4 py-3 flex justify-between items-center border-b border-borderPrimary bg-primary z-10">
          <h2 className={cn("text-16 text-textColor font-medium", medium)}>{t("common.filter")}</h2>
          <SheetClose className="flex">
            <IoClose size={24} className="text-textColor cursor-pointer" />
          </SheetClose>
        </div>

        <div className="w-full overflow-hidden flex h-full">
          {/* Categories Sidebar  */}
          <div className="w-[35%] h-full border-r border-borderPrimary bg-cardBg">
            {categories.map(
              (category) =>
                category.data.length > 0 && (
                  <button
                    key={category.key}
                    onClick={() => {
                      setActiveCategory(category.key);
                      setSearchTerms((prev) => ({ ...prev, [category.key]: prev[category.key] || "" }));
                    }}
                    className={cn(
                      "w-full px-4 py-3 text-left transition-all",
                      activeCategory === category.key
                        ? "bg-primary border-l-4 border-l-textHeadingColor "
                        : "bg-transparent hover:bg-opacity-70 border-l-4 border-l-transparent",
                    )}
                  >
                    <p
                      className={cn(
                        "text-[14px] text-textColor font-medium",
                        activeCategory === category.key && "text-textHeadingColor",
                        normalInter,
                      )}
                    >
                      {category.title}
                    </p>
                  </button>
                ),
            )}
          </div>

          <div className="flex-1 flex flex-col justify-between h-full">
            {activeCategoryData && activeCategoryData.data.length > 0 && (
              <>
                {/* Search Input */}
                <div className="w-full p-1.5">
                  <div className="w-full flex items-center border border-borderPrimary bg-cardBg p-2 rounded-lg">
                    <Search size={16} className="text-textSecondaryColor" />
                    <input
                      type="text"
                      placeholder={`Search ${activeCategoryData.title}`}
                      value={searchTerms[activeCategory] || ""}
                      onChange={(e) => setSearchTerms((prev) => ({ ...prev, [activeCategory]: e.target.value }))}
                      className={cn(
                        "w-full pl-2 bg-transparent text-textSecondaryColor text-13 placeholder:text-textSecondaryColor outline-none",
                        normalInter,
                      )}
                    />
                  </div>
                </div>

                {/* Items List */}
                <div className="h-[100%] overflow-y-auto">
                  {getFilteredItems().length > 0 ? (
                    <div className="flex-1 flex flex-col justify-start">
                      {getFilteredItems().map((item) => {
                        const isSelected = activeCategoryData.tempSelected.some((i) => i.id === item.id);
                        return (
                          <div
                            key={item.id}
                            className="flex items-center gap-3 p-2 rounded cursor-pointer transition-colors"
                            onClick={() => handleToggleItem(item)}
                          >
                            <Checkbox
                              checked={isSelected}
                              onChange={() => {}}
                              className="border-borderPrimary data-[state=checked]:bg-textColor cursor-pointer w-4 h-4"
                            />
                            <label className={cn("text-base text-textColor flex-1 cursor-pointer line-clamp-2", normalInter)} title={item.text}>
                              {item.text}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className={cn("text-base text-textColor text-opacity-50", normalInter)}>{t("common.no_results_found")}</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Footer - Apply & Clear All Buttons */}
      <div className="fixed bottom-0 left-0 right-0 px-4 py-3 bg-primary flex gap-3 justify-center items-center z-50 border-t border-borderPrimary pb-safe">
        <button
          onClick={handleClearAll}
          className={cn(
            "flex-1 py-2.5 rounded-full bg-cardBg !text-textHeadingColor text-base font-medium transition-opacity flex justify-center items-center",
            normalInter,
            "hover:bg-opacity-60",
          )}
        >
          {t("common.clear_all")}
        </button>
        <SheetClose
          asChild
          onClick={handleApplyFilters}
          className={cn(
            "flex-1 py-2.5 rounded-full bg-buttonColor !text-white text-14 font-medium transition-opacity cursor-pointer flex justify-center items-center gap-2",
            normalInter,
            "hover:bg-opacity-80",
          )}
        >
          <button className="flex justify-center items-center gap-2">
            <MdFilterList size={18} />
            {t("chopai.apply")}
          </button>
        </SheetClose>
      </div>
    </div>
  );
};

export default KirtanLyricsFilterMobile;
